import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Lightbulb, Plus, Search, Trash2, Calendar, User, Building2, MessageSquare, Send, Crown, ChevronDown, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'submitted', label: 'Submitted', color: '#6b7280' },
  { value: 'verified', label: 'Verified', color: '#3b82f6' },
  { value: 'in_process', label: 'In Process', color: '#f59e0b' },
  { value: 'fixed', label: 'Fixed', color: '#10b981' },
  { value: 'complete', label: 'Complete', color: '#8b5cf6' }
];

export default function SupportFeatureRequestsPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [requests, setRequests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [expandedRequests, setExpandedRequests] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [replyContent, setReplyContent] = useState({});
  const [sendingReply, setSendingReply] = useState({});
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: ''
  });

  const isPlatformAdmin = user?.is_platform_admin;

  useEffect(() => {
    fetchData();
  }, [searchQuery, filterCategory, filterStatus]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (filterCategory && filterCategory !== 'all') params.append('category_id', filterCategory);
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
      
      const [requestsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/support/feature-requests?${params}`),
        axios.get(`${API}/support/categories`)
      ]);
      setRequests(requestsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to load feature requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/support/feature-requests`, formData);
      toast.success('Feature request submitted');
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit feature request');
    }
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      await axios.put(`${API}/support/feature-requests/${requestId}/status`, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddReply = async (requestId) => {
    const content = replyContent[requestId];
    if (!content?.trim()) return;
    
    setSendingReply(prev => ({ ...prev, [requestId]: true }));
    try {
      await axios.post(`${API}/support/feature-requests/${requestId}/replies`, {
        content: content.trim()
      });
      setReplyContent(prev => ({ ...prev, [requestId]: '' }));
      toast.success('Reply added');
      fetchData();
    } catch (error) {
      toast.error('Failed to add reply');
    } finally {
      setSendingReply(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this feature request?')) return;
    
    try {
      await axios.delete(`${API}/support/feature-requests/${requestId}`);
      toast.success('Feature request deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete feature request');
    }
  };

  const toggleRequest = async (requestId) => {
    const isExpanding = !expandedRequests[requestId];
    setExpandedRequests(prev => ({
      ...prev,
      [requestId]: isExpanding
    }));
    
    // Fetch full details when expanding
    if (isExpanding) {
      try {
        const response = await axios.get(`${API}/support/feature-requests/${requestId}`);
        setRequests(prev => prev.map(r => r.id === requestId ? response.data : r));
      } catch (error) {
        console.error('Failed to load request details');
      }
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', category_id: '' });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusInfo = (status) => {
    return STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  };

  const getCategoryColor = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.color || '#6366f1';
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
      <div className="flex-1 overflow-auto">
        <div className="p-6 max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
                <Lightbulb className="w-8 h-8 text-green-500" />
                Feature Requests
              </h1>
              <p className="text-muted-foreground mt-1">Request new features and improvements</p>
            </div>
            <Button onClick={() => { resetForm(); setShowDialog(true); }} className="bg-green-500 text-white hover:bg-green-600" data-testid="request-feature-btn">
              <Plus className="w-4 h-4 mr-2" />
              Request Feature
            </Button>
          </div>

          {/* Filters */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search feature requests..."
                className="pl-10 bg-muted/30"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-48 bg-muted/30">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40 bg-muted/30">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUS_OPTIONS.map(status => (
                  <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Requests List */}
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Loading...</div>
          ) : requests.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="py-12 text-center">
                <Lightbulb className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No feature requests found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {requests.map(request => {
                const statusInfo = getStatusInfo(request.status);
                const isExpanded = expandedRequests[request.id];
                
                return (
                  <Card key={request.id} className="bg-card border-border hover:border-green-500/30 transition-colors">
                    <CardContent className="p-0">
                      {/* Request Header - Clickable to expand */}
                      <button
                        onClick={() => toggleRequest(request.id)}
                        className="w-full flex items-start gap-3 p-4 text-left hover:bg-muted/20 transition-colors"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1 flex-wrap">
                            <h3 className="font-semibold">{request.title}</h3>
                            <span 
                              className="px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ 
                                backgroundColor: `${statusInfo.color}20`,
                                color: statusInfo.color
                              }}
                            >
                              {statusInfo.label}
                            </span>
                            {request.category_name && (
                              <span 
                                className="px-2 py-0.5 rounded-full text-xs font-medium"
                                style={{ 
                                  backgroundColor: `${getCategoryColor(request.category_id)}20`,
                                  color: getCategoryColor(request.category_id)
                                }}
                              >
                                {request.category_name}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {request.submitted_by_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {request.submitted_by_company}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(request.created_at)}
                            </span>
                            {request.replies?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" />
                                {request.replies.length} replies
                              </span>
                            )}
                          </div>
                        </div>
                      </button>

                      {/* Request Details - Expandable */}
                      {isExpanded && (
                        <div className="px-4 pb-4 pt-0 border-t border-border/50 ml-8">
                          {/* Status Change (Admin) */}
                          {isPlatformAdmin && (
                            <div className="flex items-center gap-3 mt-3 mb-3">
                              <Label className="text-sm">Status:</Label>
                              <Select 
                                value={request.status} 
                                onValueChange={(v) => handleStatusChange(request.id, v)}
                              >
                                <SelectTrigger className="w-40 bg-muted/30 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {STATUS_OPTIONS.map(status => (
                                    <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(request.id)}
                                className="text-red-400 hover:text-red-300 ml-auto"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                          
                          {/* Description */}
                          <div className="bg-muted/30 rounded-lg p-4 mt-3">
                            <p className="whitespace-pre-wrap">{request.description}</p>
                          </div>
                          
                          {/* Replies Section */}
                          <div className="mt-4 pt-4 border-t border-border/50">
                            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                              <MessageSquare className="w-4 h-4" />
                              Discussion ({request.replies?.length || 0})
                            </h4>
                            
                            {request.replies?.length > 0 && (
                              <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                                {request.replies.map((reply, idx) => (
                                  <div key={reply.id || idx} className="bg-muted/20 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-1 text-xs">
                                      <span className="font-medium flex items-center gap-1">
                                        {reply.is_platform_admin && <Crown className="w-3 h-3 text-yellow-500" />}
                                        {reply.user_name}
                                      </span>
                                      <span className="text-muted-foreground">({reply.user_company})</span>
                                      <span className="text-muted-foreground">·</span>
                                      <span className="text-muted-foreground">{formatDate(reply.created_at)}</span>
                                    </div>
                                    <p className="text-sm whitespace-pre-wrap">{reply.content}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Add Reply */}
                            <div className="flex gap-2">
                              <textarea
                                value={replyContent[request.id] || ''}
                                onChange={(e) => setReplyContent(prev => ({ ...prev, [request.id]: e.target.value }))}
                                placeholder="Add a reply..."
                                className="flex-1 h-16 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-green-500/50 resize-none"
                              />
                            </div>
                            <div className="flex justify-end mt-2">
                              <Button
                                onClick={() => handleAddReply(request.id)}
                                disabled={!replyContent[request.id]?.trim() || sendingReply[request.id]}
                                size="sm"
                                className="bg-green-500 text-white hover:bg-green-600"
                              >
                                <Send className="w-3 h-3 mr-1" />
                                {sendingReply[request.id] ? 'Sending...' : 'Reply'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Submit Request Dialog */}
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-green-500" />
                  Request a Feature
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Brief description of the feature"
                    className="bg-muted/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Detailed description of the feature you'd like to see, use cases, expected behavior..."
                    className="w-full h-32 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-green-500/50 resize-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={formData.category_id || "none"} onValueChange={(v) => setFormData({ ...formData, category_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="bg-muted/30">
                      <SelectValue placeholder="Select category (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Category</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button onClick={handleSubmit} className="bg-green-500 text-white hover:bg-green-600" data-testid="submit-feature-request-btn">
                  Submit Request
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
